/**
 * Live ⇄ Sample: the ISP's sample workspace (practice copy with months of sample data). Entered from the sign-in page
 * ("Explore with sample data") or the user menu; the first time it is prepared in the background while this shows
 * the progress. A switch reloads the console so nothing from the other workspace stays on screen.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FlaskConical, Loader2, RotateCcw, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { useGet } from '@/shared/api/hooks';
import { apiError, core } from '@/shared/api/client';
import { can, useAuth } from '@/shared/lib/auth';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useT } from '@/i18n';

type Entry = { preparing?: boolean; ticket?: string; progress?: number; step?: string; error?: string | null; accessToken?: string };

/** Runs a request that opens a workspace: a session goes straight in; "preparing" shows the progress until it is ready. */
export function useWorkspaceEntry(next = '/') {
  const setSession = useAuth((s) => s.setSession);
  const [wait, setWait] = useState<Entry | null>(null);
  const go = (data: Entry) => {
    if (data.accessToken) { setSession(data as any); location.assign(next); return; }
    setWait(data);
  };
  useEffect(() => {
    if (!wait?.preparing || wait.error) return;
    const id = setTimeout(async () => {
      try { go((await axios.post('/api/core/auth/sample/wait', { ticket: wait.ticket })).data); } catch (e) { setWait({ ...wait, error: apiError(e) }); }
    }, 2000);
    return () => clearTimeout(id);
  }, [wait]); // eslint-disable-line react-hooks/exhaustive-deps
  const overlay = <PreparingDialog entry={wait} onClose={() => setWait(null)} />;
  return { go, overlay, busy: !!wait };
}

function PreparingDialog({ entry, onClose }: { entry: Entry | null; onClose: () => void }) {
  const t = useT();
  const started = useRef(Date.now());
  useEffect(() => { if (entry && !entry.progress) started.current = Date.now(); }, [entry]);
  if (!entry) return null;
  const pct = Math.max(3, entry.progress ?? 0);
  return (
    <Dialog open onOpenChange={(o) => { if (!o && entry.error) onClose(); }}>
      <DialogContent className="max-w-md">
        <div className="space-y-4 py-2 text-center">
          <FlaskConical className="mx-auto size-9 text-violet-500" />
          <div><h2 className="text-lg font-bold">{t('sample.preparing_title')}</h2><p className="mt-1 text-sm text-muted-foreground">{t('sample.preparing_body')}</p></div>
          {entry.error ? <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-600">{entry.error}</div> : <>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-violet-600 transition-all duration-700" style={{ width: `${pct}%` }} /></div>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" />{t(`sample.step.${entry.step ?? 'starting'}`, { defaultValue: t('sample.step.starting') })} · {pct}%</div>
          </>}
          {entry.error && <Button variant="outline" onClick={onClose}>{t('sample.close')}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** User-menu entries: open the sample, go back to live, reset the sample. */
export function WorkspaceMenu({ onDone }: { onDone: () => void }) {
  const t = useT();
  const user = useAuth((s) => s.user);
  const ws = useGet<any>('/workspace', undefined, { staleTime: 60_000 });
  const { go, overlay } = useWorkspaceEntry();
  const [confirm, setConfirm] = useState(false);
  const sample = user?.tenant?.kind === 'sandbox';
  const run = async (fn: () => Promise<any>) => { onDone(); try { go((await fn()).data); } catch (e) { toast.error(apiError(e)); } };
  if (!sample && !ws.data?.sampleAvailable) return overlay;
  return (
    <>
      {sample ? <>
        <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-muted" onClick={() => run(() => core.post('/workspace/switch', { to: 'live' }))}><Undo2 className="size-4" /> {t('sample.to_live')}</button>
        {can(user, 'admin.settings') && <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-muted" onClick={() => setConfirm(true)}><RotateCcw className="size-4" /> {t('sample.reset')}</button>}
      </> : <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-muted" onClick={() => run(() => core.post('/workspace/switch', { to: 'sample' }))}><FlaskConical className="size-4 text-violet-500" /> {t('sample.to_sample')}</button>}
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-sm">
          <div className="space-y-3"><h2 className="text-base font-bold">{t('sample.reset_title')}</h2><p className="text-sm text-muted-foreground">{t('sample.reset_body')}</p>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setConfirm(false)}>{t('sample.cancel')}</Button>
              <Button onClick={() => { setConfirm(false); run(() => core.post('/workspace/sample/reset', {})); }}><RotateCcw className="size-4" /> {t('sample.reset')}</Button></div></div>
        </DialogContent>
      </Dialog>
      {overlay}
    </>
  );
}
